/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { Link } from "react-router-dom";

import { CommandBarButton, Stack, StackItem, Text } from "@fluentui/react";

import { UpdatePrimaryEmailPanel } from "Components/UpdatePrimaryEmailPanel/UpdatePrimaryEmailPanel";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { getTenant } from "Utility/Utils";

import { IProfileHeaderProps } from "./IProfileHeaderProps";
import {
  bolderTextStyles,
  contentStackStyles,
  contentStackTokens,
  headerStackStyles,
  headerStackTokens,
  isCompetitorTextStyles,
  normalTextStyles,
  privateProfileTextStyles,
  updateButtonStyles,
} from "./ProfileHeader.styles";

const ProfileHeader = (props: IProfileHeaderProps): React.ReactElement => {
  const {
    userProfileId,
    firstName,
    lastName,
    primaryEmail,
    secondaryEmail,
    tenants,
    currentTenant,
    isPrivate,
    isOvdfBlock,
    technologyAreas,
    yearsInProgram,
    isCompetitor,
    companyName,
    jobTitle,
    canUpdatePrimaryEmail,
    updatePrimaryEmailStatus,
    updatePrimaryEmailError,
    onPrimaryEmailUpdate,
    onPrimaryEmailUpdateReset,
    loadUserProfile,
  } = props;

  const [isUpdatePrimaryEmailPanelOpen, setIsUpdatePrimaryEmailPanelOpen] = React.useState(false);

  const renderTenantLinks = (): JSX.Element[] | undefined => {
    if (!tenants?.length || tenants?.length <= 1) return undefined;

    return tenants.map((tenant, index) => {
      const tenantName = getTenant(tenant);
      const tenantDisplayName: string =
        tenantName === TenantTypesEnum.MLSA ? "Student" : tenantName;

      return (
        <React.Fragment key={tenant}>
          {tenant === currentTenant ? (
            <span>{tenantDisplayName}</span>
          ) : (
            <Link to={`/${tenantName.toLowerCase()}/details/${userProfileId}`} replace>
              {tenantDisplayName}
            </Link>
          )}
          {index < tenants.length - 1 ? " | " : null}
        </React.Fragment>
      );
    });
  };

  return (
    <Stack styles={headerStackStyles} tokens={headerStackTokens}>
      <Stack horizontal tokens={contentStackTokens}>
        <StackItem styles={contentStackStyles}>
          <Text styles={bolderTextStyles}>{`${firstName} ${lastName} [${userProfileId}]`}</Text>
        </StackItem>
        {!!yearsInProgram && (
          <StackItem styles={contentStackStyles}>
            <Text styles={bolderTextStyles}>{`Years in Program: ${yearsInProgram}`}</Text>
          </StackItem>
        )}
        {!!isCompetitor && (
          <StackItem styles={contentStackStyles}>
            <Text styles={isCompetitorTextStyles}>{"May work for a competitor"}</Text>
          </StackItem>
        )}
      </Stack>
      <Stack horizontal tokens={contentStackTokens}>
        <Stack styles={contentStackStyles}>
          <Stack horizontal tokens={contentStackTokens}>
            <StackItem>
              <Text styles={normalTextStyles}>{`Login Email: ${primaryEmail}`}</Text>
            </StackItem>
            {!!canUpdatePrimaryEmail && (
              <CommandBarButton
                onClick={() => setIsUpdatePrimaryEmailPanelOpen(true)}
                styles={updateButtonStyles}
                text="Update"
                iconProps={{ iconName: "Edit" }}
              ></CommandBarButton>
            )}
          </Stack>
          {!!secondaryEmail && (
            <StackItem>
              <Text styles={normalTextStyles}>{`Contact Email: ${secondaryEmail}`}</Text>
            </StackItem>
          )}
          <StackItem tokens={contentStackTokens}>{renderTenantLinks()}</StackItem>
          {!!isPrivate && (
            <StackItem>
              <Text styles={privateProfileTextStyles}>{"Private Profile"}</Text>
            </StackItem>
          )}
          {isOvdfBlock && (
            <StackItem>
              <Text styles={privateProfileTextStyles}>{"OVDF Block"}</Text>
            </StackItem>
          )}
        </Stack>
        <Stack styles={contentStackStyles}>
          {technologyAreas?.map((technologyArea, index) => (
            <StackItem key={technologyArea.technologyFocusAreaName}>
              <Text styles={normalTextStyles}>{`Technology Area ${index + 1}: ${
                technologyArea.technologyFocusAreaName
              }`}</Text>
            </StackItem>
          ))}
        </Stack>
        <Stack styles={contentStackStyles}>
          {!!jobTitle && (
            <StackItem>
              <Text styles={normalTextStyles}>{`Job Title: ${jobTitle}`}</Text>
            </StackItem>
          )}
          {!!companyName && (
            <StackItem>
              <Text
                styles={normalTextStyles}
              >{`Current Employer/Organization: ${companyName}`}</Text>
            </StackItem>
          )}
        </Stack>
      </Stack>
      <UpdatePrimaryEmailPanel
        isOpen={isUpdatePrimaryEmailPanelOpen}
        setIsOpen={setIsUpdatePrimaryEmailPanelOpen}
        userProfileId={userProfileId}
        updatePrimaryEmailStatus={updatePrimaryEmailStatus}
        updatePrimaryEmailError={updatePrimaryEmailError}
        onPrimaryEmailUpdate={onPrimaryEmailUpdate}
        onPrimaryEmailUpdateReset={onPrimaryEmailUpdateReset}
        loadUserProfile={loadUserProfile}
      />
    </Stack>
  );
};

export default ProfileHeader;
